import { Ajuda } from '../../design/Ajuda';
import { useCallback, useEffect, useState } from 'react';
import type { Sessao } from '../../auth';
import { ErroDeArea } from '../../design/Estados';
import { Icone } from '../../design/Icone';
import {
  bN,
  cancelar,
  diaCurto,
  diaDaSemana,
  gerar,
  hojeBrasilia,
  lerConfigDoGiro,
  lerEquipe,
  lerOperacao,
  encerrarVinculo,
  criarSetoresPadrao,
  lerSetores,
  publicar,
  salvarSetor,
  simular,
  vincular,
} from '../../giro';
import { listarEquipe } from '../../acesso';
import type { MembroDaEquipe } from '../../acesso';
import type {
  ConfigDoGiro,
  EquipeDoGiro,
  OperacaoDoDia,
  PlanoDoDia,
  SetorDoGiro,
} from '../../giro';
import { cancelarEmSequencia, selecaoAposSalvar, setoresParaGerar } from '../../giro-gestor';
import { hora } from '../../vendas';
import { QuadroDeBlocos } from './QuadroDeBlocos';
import { SubnavDoGiro } from './SubnavDoGiro';

/**
 * TELA 17 — GERAÇÃO E CONFIGURAÇÃO. "Ninguém publica sem ver antes."
 *
 *   simular → revisar → gerar (rascunho) → publicar
 *
 * A simulação não toca em nada (não cria operação, não consome rodízio). O selo SIMULAÇÃO acompanha
 * a tela inteira, em fundo diferente. D-1 explícito: "Operação 22/09 · vendas de 21/09", nunca
 * "ontem". Configuração não retroage: vale para as próximas gerações.
 *
 * PRÉVIA DE AMANHÃ (dono, 27/09): o mesmo simular com o dia seguinte — mostra como o Giro de
 * amanhã está ficando com as vendas lidas ATÉ AGORA. Só simula: sem gerar, sem publicar, sem
 * cancelar (o Giro de amanhã quem monta é o automático, com o dia de hoje fechado).
 */

export function GeracaoDoGiro({ sessao }: { sessao: Sessao }) {
  const hoje = hojeBrasilia();
  const amanha = diaSeguinte(hoje);
  const [previa, setPrevia] = useState(false);
  const dia = previa ? amanha : hoje;
  const [setores, setSetores] = useState<readonly SetorDoGiro[] | null>(null);
  const [op, setOp] = useState<OperacaoDoDia | null>(null);
  const [equipe, setEquipe] = useState<EquipeDoGiro | null>(null);
  const [marcados, setMarcados] = useState<Set<string>>(new Set());
  const [planos, setPlanos] = useState<Map<string, PlanoDoDia>>(new Map());
  const [aba, setAba] = useState<string | null>(null);
  const [simuladoEm, setSimuladoEm] = useState<string | null>(null);
  const [trabalhando, setTrabalhando] = useState(false);
  const [falha, setFalha] = useState<string | null>(null);
  const [erro, setErro] = useState(false);
  const [motivo, setMotivo] = useState('');
  const [cancelando, setCancelando] = useState(false);
  /** Modal de cancelamento aberto: as operações que ele vai cancelar (fotografadas ao abrir). */
  const [aCancelar, setACancelar] = useState<readonly { id: string; nome: string }[] | null>(null);
  const [falhaCancelar, setFalhaCancelar] = useState<string | null>(null);
  /** O gestor viu que o rascunho saiu de vendas lidas pela metade e quer publicar assim mesmo. */
  const [confirmaIncompletas, setConfirmaIncompletas] = useState(false);

  const carregar = useCallback(() => {
    setErro(false);
    void Promise.all([lerSetores(), lerOperacao(dia), lerEquipe(dia)]).then(([s, o, e]) => {
      if (!s.ok || !o.ok) return setErro(true);
      const ativos = s.dados.setores.filter((x) => x.ativo && x.gera);
      setSetores(ativos);
      setOp(o.dados);
      if (e.ok) setEquipe(e.dados);
      // Seleção só do que ainda pode gerar: setor que ganhou rascunho/publicação sai da seleção
      // (a caixa fica desmarcada e desabilitada — não pode continuar valendo escondida).
      setMarcados(
        (m) =>
          new Set(setoresParaGerar(m.size ? m : new Set(ativos.map((x) => x.id)), ativos, o.dados)),
      );
    });
  }, [dia]);
  useEffect(carregar, [carregar]);
  const trocarDia = (paraAmanha: boolean) => {
    if (paraAmanha === previa || trabalhando) return;
    setPlanos(new Map());
    setMarcados(new Set());
    setOp(null);
    setFalha(null);
    setAba(null);
    setPrevia(paraAmanha);
  };

  const rascunhos = op?.setores.filter((s) => s.geracao?.estado === 'RASCUNHO') ?? [];
  const paraGerar = setoresParaGerar(marcados, setores ?? [], op);
  const publicadas = op?.setores.filter((s) => s.geracao?.estado === 'PUBLICADA') ?? [];

  const simularTudo = () => {
    setTrabalhando(true);
    setFalha(null);
    void Promise.all(
      paraGerar.map((id) => simular(sessao, id, dia).then((r) => [id, r] as const)),
    ).then((res) => {
      setTrabalhando(false);
      const m = new Map<string, PlanoDoDia>();
      for (const [id, r] of res) {
        if (r.tipo === 'confirmada') m.set(id, r.dados);
        else if (r.tipo === 'recusada') setFalha(r.mensagem);
        else setFalha('Sem resposta do servidor. A simulação não grava nada — pode repetir.');
      }
      setPlanos(m);
      setSimuladoEm(new Date().toISOString());
      setAba((a) => (a && m.has(a) ? a : ([...m.keys()][0] ?? null)));
    });
  };

  const gerarRascunhos = () => {
    setTrabalhando(true);
    setFalha(null);
    void (async () => {
      for (const id of setoresParaGerar(new Set(planos.keys()), setores ?? [], op)) {
        const r = await gerar(sessao, id, dia);
        if (r.tipo === 'recusada') setFalha(r.mensagem);
        else if (r.tipo === 'incerta')
          setFalha(
            'Sem resposta do servidor: o rascunho pode ter sido criado. Recarregue antes de repetir.',
          );
      }
      setTrabalhando(false);
      setPlanos(new Map());
      carregar();
    })();
  };

  // Rascunhos montados com o último dia da captura lido pela metade: publicar exige confirmar.
  const incompletos = rascunhos.filter((s) => s.geracao?.vendasIncompletas);
  const publicarTodos = () => {
    if (incompletos.length && !confirmaIncompletas)
      return setFalha('Confirme que quer publicar com as vendas incompletas.');
    setTrabalhando(true);
    setFalha(null);
    void (async () => {
      for (const s of rascunhos) {
        const r = await publicar(
          sessao,
          s.geracao?.id ?? '',
          confirmaIncompletas && !!s.geracao?.vendasIncompletas,
        );
        if (r.tipo === 'recusada') setFalha(r.mensagem);
        else if (r.tipo === 'incerta')
          setFalha(
            'Sem resposta do servidor: a operação pode ter sido publicada. Recarregue antes de repetir.',
          );
      }
      setTrabalhando(false);
      setConfirmaIncompletas(false);
      carregar();
    })();
  };

  const abrirCancelamento = () => {
    setFalhaCancelar(null);
    setACancelar(
      [...publicadas, ...rascunhos].flatMap((s) =>
        s.geracao ? [{ id: s.geracao.id, nome: s.nome }] : [],
      ),
    );
  };
  const fecharCancelamento = () => {
    if (cancelando) return;
    setACancelar(null);
    setFalhaCancelar(null);
  };
  const cancelarTodas = () => {
    const alvos = aCancelar ?? [];
    if (!alvos.length) return;
    if (motivo.trim().length < 5)
      return setFalhaCancelar('O motivo é obrigatório para cancelar (mínimo 5 caracteres).');
    setCancelando(true);
    setFalhaCancelar(null);
    void cancelarEmSequencia(alvos, (id) => cancelar(sessao, id, motivo.trim())).then((r) => {
      setCancelando(false);
      if (r.erro) {
        setFalhaCancelar(r.erro);
        setACancelar(r.faltam);
      } else {
        // o motivo só some quando TUDO foi cancelado: com erro, fica para repetir
        setMotivo('');
        setACancelar(null);
      }
      carregar();
    });
  };

  const plano = aba ? planos.get(aba) : undefined;
  const todos = [...planos.values()];
  const soma = (f: (p: PlanoDoDia) => number) => todos.reduce((a, p) => a + f(p), 0);
  const vendidosTotais = todos[0] ? soma((p) => p.vendidos) : null;

  return (
    <section className="cv gr">
      <SubnavDoGiro
        atual="giro-geracao"
        direita={setores ? `config vigente v${String(setores[0]?.versao ?? 1)}` : undefined}
      />
      {planos.size > 0 && (
        <div className="gr-simulacao">
          <span className="gr-selo-sim">
            <Icone nome="warn" tamanho={12} traco={1.8} />{' '}
            {previa ? 'PRÉVIA DE AMANHÃ' : 'SIMULAÇÃO'}
          </span>
          {previa
            ? `Como o Giro de ${diaCurto(dia)} está ficando com as vendas lidas até agora — pode mudar até o dia fechar.`
            : 'Esta simulação não altera o rodízio nem cria uma operação real.'}
          <span style={{ flex: 1 }} />
          <em>gerada às {simuladoEm ? hora(simuladoEm) : ''} · válida enquanto a base não mudar</em>
        </div>
      )}
      {erro ? (
        <div className="cv-corpo">
          <ErroDeArea oQue="a geração" aoTentar={carregar} />
        </div>
      ) : !setores || !op ? (
        <div className="cv-corpo">
          <div className="cv-carregando" aria-busy="true" />
        </div>
      ) : (
        <div className={`gr-ger ${planos.size ? 'gr-ger-sim' : ''}`}>
          <aside className="gr-ger-form">
            <h1>{previa ? 'Prévia de amanhã' : 'Nova geração'}</h1>
            <p className="cv-cinza cv-peq">
              {previa
                ? 'só simula · nada é gravado · amanhã o Giro automático gera sozinho'
                : '4 passos · nada é aplicado antes de publicar'}
            </p>
            <span className="gr-rotulo">
              DATA DA OPERAÇÃO
              <Ajuda rotulo="a data da operação">
                Hoje monta o Giro de verdade. Amanhã é só uma prévia: mostra como ficaria com as
                vendas lidas até agora, sem gravar nada — o Giro de amanhã sai sozinho de manhã.
              </Ajuda>
            </span>
            <div className="cv-segmento" role="tablist" aria-label="Dia da operação">
              <button
                type="button"
                role="tab"
                aria-selected={!previa}
                className={!previa ? 'cv-sel' : ''}
                disabled={trabalhando}
                onClick={() => trocarDia(false)}
              >
                Hoje {diaCurto(hoje)}
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={previa}
                className={previa ? 'cv-sel' : ''}
                disabled={trabalhando}
                onClick={() => trocarDia(true)}
              >
                Amanhã {diaCurto(amanha)} · prévia
              </button>
            </div>
            <div className="gr-campo">
              <Icone nome="cal" tamanho={15} traco={1.6} />
              <b>
                {diaCurto(dia)}/{dia.slice(0, 4)}
              </b>
              <span style={{ flex: 1 }} />
              <em>{diaDaSemana(dia).split('-')[0]}</em>
            </div>
            <div className="gr-campo gr-campo-info">
              <Icone nome="receipt" tamanho={15} traco={1.6} />
              <span>
                Vendas utilizadas:{' '}
                <b>
                  {op.vendasDesde < op.vendasDe
                    ? `${diaCurto(op.vendasDesde)} a ${diaCurto(op.vendasDe)}/${op.vendasDe.slice(0, 4)}`
                    : `${diaCurto(op.vendasDe)}/${op.vendasDe.slice(0, 4)} (D-1)`}
                </b>
                {vendidosTotais !== null
                  ? ` · ${String(vendidosTotais)} SKUs distintos nos setores`
                  : ''}
                <Ajuda rotulo="vendas utilizadas">
                  O Giro usa as vendas do dia anterior (na segunda, de sábado e domingo). Só entra
                  produto que vendeu nesse período.
                </Ajuda>
                <br />
                <em className={op.vendasLidas.completa ? '' : 'gr-ambar'}>
                  {op.vendasLidas.completa
                    ? op.vendasLidas.lidasAte
                      ? `vendas lidas até ${diaCurto(dataBr(op.vendasLidas.lidasAte))} ${hora(op.vendasLidas.lidasAte)} · dia completo`
                      : 'dia completo'
                    : op.vendasLidas.aviso}
                </em>
              </span>
            </div>
            {!op.vendasLidas.completa && previa && (
              <div className="gr-avisos" role="status">
                <Icone nome="warn" tamanho={15} traco={1.7} />
                <p>
                  <b>Vendas de hoje ainda entrando</b> — a prévia usa o que já foi lido do HOS. O
                  Giro de verdade sai amanhã, sozinho, depois que a leitura fechar o dia.
                </p>
              </div>
            )}
            {!op.vendasLidas.completa && !previa && (
              <div className="gr-avisos" role="status">
                <Icone nome="warn" tamanho={15} traco={1.7} />
                <p>
                  <b>Vendas incompletas</b> — {op.vendasLidas.aviso} Dá para simular e gerar o
                  rascunho, mas publicar vai pedir confirmação. A geração automática espera a
                  leitura fechar o dia.
                </p>
              </div>
            )}
            <span className="gr-rotulo">
              FONTE DE ESTOQUE
              <Ajuda rotulo="a fonte de estoque">
                O estoque vem do HOS na hora da geração. Produto sem leitura confiável fica fora do
                Giro — o sistema nunca trata &quot;sem leitura&quot; como zero.
              </Ajuda>
            </span>
            <div className="gr-campo gr-campo-fonte">
              <i
                className={
                  op.estoqueCapturadoAte || todos[0]?.fonteEstoque.capturadoAte || op.estoqueLidoEm
                    ? 'gr-ok'
                    : 'gr-off'
                }
              />
              <div>
                <b>Estoque do HOS</b>
                <em>
                  {todos[0]?.fonteEstoque.capturadoAte
                    ? `atualizado ${hora(todos[0].fonteEstoque.capturadoAte)}`
                    : op.estoqueCapturadoAte
                      ? `atualizado ${hora(op.estoqueCapturadoAte)}`
                      : op.estoqueLidoEm
                        ? `última leitura do HOS ${hora(op.estoqueLidoEm)}`
                        : 'ainda sem leitura do HOS'}
                  {todos.length
                    ? ` · ${String(soma((p) => p.fonteEstoque.indisponiveis))} itens sem leitura confiável`
                    : ''}
                </em>
              </div>
              <span className="gr-tag">API DO HOS</span>
            </div>
            <span className="gr-rotulo">
              SETORES
              <Ajuda rotulo="os setores">
                Marque os setores que vão ter Giro. Setor que já tem operação hoje aparece travado
                (rascunho ou publicada). &quot;0 presentes&quot; = ninguém do setor veio.
              </Ajuda>
            </span>
            {setores.length === 0 && (
              <p className="cv-cinza cv-peq">
                Nenhum setor ativo. <a href="#/giro-setores">Configurar setores</a>
              </p>
            )}
            {setores.map((s) => {
              const ger = op.setores.find((x) => x.setorId === s.id)?.geracao;
              const pres =
                equipe?.pessoas.filter(
                  (p) => p.setores.some((x) => x.id === s.id) && p.presenca !== 'AUSENTE',
                ).length ?? 0;
              const ja = ger && ger.estado !== 'CANCELADA';
              return (
                <label
                  key={s.id}
                  className={`gr-check ${!pres ? 'gr-check-alerta' : ''} ${ja ? 'gr-check-off' : ''}`}
                >
                  <input
                    type="checkbox"
                    disabled={!!ja}
                    checked={marcados.has(s.id) && !ja}
                    onChange={(e) =>
                      setMarcados((m) => {
                        const n = new Set(m);
                        if (e.target.checked) n.add(s.id);
                        else n.delete(s.id);
                        return n;
                      })
                    }
                  />
                  <b>{s.nome}</b>
                  <span style={{ flex: 1 }} />
                  <em>
                    {ja
                      ? ger.estado === 'RASCUNHO'
                        ? 'rascunho'
                        : 'publicada'
                      : !pres
                        ? '0 presentes'
                        : `${String(s.qtdBlocos)} blocos`}
                  </em>
                </label>
              );
            })}
            <div className="gr-rotulo gr-rotulo-linha">
              <span>
                {previa ? `PRESENÇA DE ${diaCurto(dia)}` : 'PRESENÇA DE HOJE'}
                <Ajuda rotulo="a presença">
                  Só quem está presente recebe bloco. Meio período recebe menos. Ajuste em Equipe
                  &amp; presença antes de gerar.
                </Ajuda>
              </span>
              <a href="#/giro-equipe">Revisar</a>
            </div>
            <a className="gr-campo gr-campo-link" href="#/giro-equipe">
              <div>
                <b>
                  {equipe?.presentes ?? '—'} presentes de {equipe?.vinculados ?? '—'}
                </b>
                <em>
                  {equipe?.ausentes ?? 0} ausentes · {equipe?.parciais ?? 0} parcial
                </em>
              </div>
              <Icone nome="chev-r" tamanho={14} traco={1.6} />
            </a>
            <button
              type="button"
              className="cv-botao gr-botao-largo"
              disabled={trabalhando || !paraGerar.length}
              onClick={simularTudo}
            >
              <Icone nome="warn" tamanho={14} traco={1.7} />{' '}
              {planos.size
                ? 'Simular novamente'
                : previa
                  ? `Ver prévia de ${diaCurto(dia)}`
                  : 'Simular geração'}
            </button>
            {falha && (
              <p className="cv-falha" role="alert">
                {falha}
              </p>
            )}
          </aside>

          <div className="gr-ger-res">
            {planos.size > 0 && plano ? (
              <>
                <div className="cv-kpis">
                  <Mini rotulo="SETORES" valor={planos.size} />
                  <Mini rotulo="BLOCOS" valor={soma((p) => p.blocos.length)} />
                  <Mini rotulo="ELEGÍVEIS" valor={soma((p) => p.elegiveis)} verde />
                  <Mini
                    rotulo="ESTOQUE ZERO"
                    valor={soma(
                      (p) => p.exclusoes.filter((e) => e.motivo === 'ESTOQUE_ZERO').length,
                    )}
                  />
                  <Mini
                    rotulo="AGUARDANDO DADOS"
                    valor={soma(
                      (p) =>
                        p.exclusoes.filter(
                          (e) =>
                            e.motivo === 'ESTOQUE_INDISPONIVEL' || e.motivo === 'ESTOQUE_STALE',
                        ).length,
                    )}
                    ambar
                  />
                  <Mini
                    rotulo="SEM SETOR · CADASTRO"
                    valor={`${String(todos[0]?.exclusoes.filter((e) => e.motivo === 'SEM_SETOR').length ?? 0)} · ${String(todos[0]?.exclusoes.filter((e) => e.motivo === 'SEM_CADASTRO').length ?? 0)}`}
                  />
                </div>
                {planos.size > 1 && (
                  <div className="cv-segmento gr-abas-setor" role="tablist">
                    {todos.map((p) => (
                      <button
                        key={p.setor.id}
                        type="button"
                        role="tab"
                        aria-selected={aba === p.setor.id}
                        className={aba === p.setor.id ? 'cv-sel' : ''}
                        onClick={() => setAba(p.setor.id)}
                      >
                        {p.setor.nome}
                      </button>
                    ))}
                  </div>
                )}
                <div className="cv-cartao cv-sem-pad">
                  <div className="cv-cartao-topo cv-pad">
                    <h2>
                      {plano.setor.nome} · {plano.elegiveis} SKUs em {plano.blocos.length} blocos{' '}
                      <span className="cv-cinza gr-normal">
                        carga média-alvo {String(plano.cargaAlvo).replace('.', ',')} SKUs/bloco —
                        estimativa, não obrigação
                      </span>
                    </h2>
                    <span className="cv-cinza cv-peq">
                      {plano.config.permitirQuebraLetra
                        ? 'quebra de letra ligada'
                        : 'fronteiras preservam letras inteiras'}
                    </span>
                  </div>
                  <div className="cv-rolagem">
                    <table className="cv-tabela">
                      <thead>
                        <tr>
                          <th>Bloco</th>
                          <th>Faixa</th>
                          <th>Primeiro → último produto</th>
                          <th className="cv-dir">SKUs</th>
                          <th>Carga</th>
                          <th>Responsável sugerido</th>
                        </tr>
                      </thead>
                      <tbody>
                        {plano.blocos.map((b) => {
                          const max = Math.max(...plano.blocos.map((x) => x.total), 1);
                          return (
                            <tr key={b.numero} className={b.letraQuebrada ? 'gr-linha-ambar' : ''}>
                              <td className="cv-forte">{bN(b.numero)}</td>
                              <td>
                                <span
                                  className={`gr-faixa ${b.letraQuebrada ? 'gr-faixa-ambar' : ''}`}
                                >
                                  {b.rotulo}
                                </span>
                              </td>
                              <td className="cv-cinza gr-td-faixa">
                                {b.total ? `${b.primeiro ?? ''} → ${b.ultimo ?? ''}` : '—'}
                              </td>
                              <td className="cv-dir cv-forte">{b.total}</td>
                              <td>
                                <div className="gr-carga">
                                  <i
                                    className={
                                      b.total > plano.cargaAlvo * 1.3 ? 'gr-carga-ambar' : ''
                                    }
                                    style={{ width: `${String((b.total / max) * 100)}%` }}
                                  />
                                </div>
                              </td>
                              <td>
                                {b.responsavel ? (
                                  <>
                                    <b>{b.responsavel.nome}</b>{' '}
                                    <span className="cv-cinza cv-peq">
                                      {b.segundoBloco
                                        ? `2º bloco · ${String(plano.pessoas.filter((p) => p.estado !== 'AUSENTE').length)} presentes`
                                        : 'sugerido'}
                                    </span>
                                  </>
                                ) : b.total ? (
                                  <span className="gr-vermelho cv-peq">
                                    sem responsável disponível
                                  </span>
                                ) : (
                                  <span className="cv-cinza cv-peq">sem carga hoje</span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  {plano.avisos.length > 0 && (
                    <div className="gr-avisos">
                      <Icone nome="warn" tamanho={15} traco={1.7} />
                      <p>
                        <b>Avisos da simulação</b> — {plano.avisos.join(' ')}
                      </p>
                    </div>
                  )}
                </div>
                <div className="cv-cartao">
                  <div className="cv-cartao-topo">
                    <h2>Rodízio no ciclo · {plano.setor.nome}</h2>
                    <span className="cv-cinza cv-peq">✓ = concluído, não atribuído</span>
                  </div>
                  <div className="gr-rod-mini">
                    {plano.pessoas
                      .filter((p) => p.estado !== 'AUSENTE')
                      .map((p) => (
                        <div key={p.accountId}>
                          <b>{p.nome}</b>
                          <span className="gr-casas">
                            {Array.from({ length: plano.config.qtdBlocos }, (_, i) => (
                              <i
                                key={i}
                                className={p.concluidos.includes(i + 1) ? 'gr-casa-ok' : ''}
                              >
                                {bN(i + 1)}
                              </i>
                            ))}
                          </span>
                          <em>
                            {p.concluidos.length}/{plano.config.qtdBlocos} · elegíveis{' '}
                            {Array.from({ length: plano.config.qtdBlocos }, (_, i) => i + 1)
                              .filter((n) => !p.concluidos.includes(n))
                              .map(bN)
                              .join(', ') || 'volta nova'}
                          </em>
                        </div>
                      ))}
                  </div>
                </div>
                {previa ? (
                  <div className="gr-ger-acoes">
                    <span className="cv-cinza cv-peq">
                      Só prévia: nada foi gravado. Amanhã o Giro automático monta a operação com as
                      vendas de {diaCurto(plano.vendasDe)} completas — produtos e blocos podem
                      mudar.
                    </span>
                  </div>
                ) : (
                  <div className="gr-ger-acoes">
                    <button
                      type="button"
                      className="cv-botao-primario"
                      disabled={trabalhando}
                      onClick={gerarRascunhos}
                    >
                      Gerar rascunho
                    </button>
                    <span className="cv-cinza cv-peq">
                      O rascunho congela a foto do dia. A equipe só vê depois de publicar.
                    </span>
                  </div>
                )}
              </>
            ) : (
              <>
                {!previa && rascunhos.length > 0 && (
                  <div className="cv-cartao gr-rascunho">
                    <div className="gr-rascunho-topo">
                      <span className="gr-tag">RASCUNHO</span>
                      <span className="cv-mono cv-cinza cv-peq">
                        gerada {rascunhos[0]?.geracao ? hora(rascunhos[0].geracao.criadoEm) : ''}
                      </span>
                    </div>
                    <h2>Operação de {diaCurto(dia)} pronta para publicar</h2>
                    <p className="cv-cinza">
                      {rascunhos.length} setor(es) ·{' '}
                      {rascunhos.reduce((a, s) => a + s.blocos.length, 0)} blocos ·{' '}
                      {rascunhos.reduce((a, s) => a + (s.geracao?.elegiveis ?? 0), 0)} SKUs · vendas
                      de {diaCurto(op.vendasDe)}
                    </p>
                    <h3>Ao publicar, ficam congelados nesta operação</h3>
                    <ul className="gr-congela">
                      <li>
                        <Icone nome="check" tamanho={13} traco={2} />{' '}
                        {rascunhos.reduce((a, s) => a + (s.geracao?.elegiveis ?? 0), 0)} produtos
                        elegíveis
                      </li>
                      <li>
                        <Icone nome="check" tamanho={13} traco={2} />{' '}
                        {rascunhos.reduce((a, s) => a + s.blocos.length, 0)} blocos e suas faixas
                      </li>
                      <li>
                        <Icone nome="check" tamanho={13} traco={2} /> Responsáveis atribuídos
                      </li>
                      <li>
                        <Icone nome="check" tamanho={13} traco={2} /> Alvos de exposição por item
                      </li>
                      <li>
                        <Icone nome="check" tamanho={13} traco={2} /> Snapshot de estoque{' '}
                        {op.estoqueCapturadoAte
                          ? `de ${hora(op.estoqueCapturadoAte)}`
                          : 'da geração'}
                      </li>
                      <li>
                        <Icone nome="check" tamanho={13} traco={2} /> Configuração vigente dos
                        setores
                      </li>
                    </ul>
                    <p className="gr-nota">
                      Alterações de configuração feitas depois disso valem só para as próximas
                      gerações. O rodízio continua intocado: ele avança quando cada bloco for{' '}
                      <b>concluído</b>.
                    </p>
                    {incompletos.length > 0 && (
                      <div className="gr-avisos" role="alert">
                        <Icone nome="warn" tamanho={15} traco={1.7} />
                        <div>
                          <p>
                            <b>Rascunho montado com vendas incompletas</b> —{' '}
                            {incompletos
                              .map((s) => `${s.nome}: ${s.geracao?.vendasIncompletas ?? ''}`)
                              .join(' ')}
                          </p>
                          <p className="cv-peq">
                            O mais seguro é cancelar e gerar de novo depois que a leitura do HOS
                            fechar o dia.
                          </p>
                          <label className="gr-check">
                            <input
                              type="checkbox"
                              checked={confirmaIncompletas}
                              onChange={(e) => setConfirmaIncompletas(e.target.checked)}
                            />
                            <b>Publicar mesmo assim, sabendo que as vendas estão incompletas</b>
                          </label>
                        </div>
                      </div>
                    )}
                    <div className="gr-modal-acoes">
                      <button
                        type="button"
                        className="cv-botao-primario gr-botao-grande"
                        disabled={trabalhando || (incompletos.length > 0 && !confirmaIncompletas)}
                        onClick={publicarTodos}
                      >
                        {trabalhando ? 'Publicando…' : 'Publicar para a equipe'}
                      </button>
                      <a className="cv-botao gr-botao-link" href="#/giro-operacao">
                        Revisar blocos
                      </a>
                    </div>
                  </div>
                )}
                {!previa && publicadas.length > 0 && (
                  <div className="cv-cartao gr-cancelar">
                    <h2>
                      <Icone nome="warn" tamanho={16} traco={1.8} /> Cancelar a operação de hoje
                      <Ajuda rotulo="cancelar a operação">
                        Para o Giro de hoje para todo mundo. O que já foi contado e reposto fica
                        guardado no histórico. Pede motivo e fica registrado quem cancelou.
                      </Ajuda>
                    </h2>
                    <p>
                      {op.progresso.blocosConcluidos} blocos já concluídos e {op.progresso.contados}{' '}
                      itens contados continuam no histórico. A equipe perde o acesso à missão
                      imediatamente.
                    </p>
                    <div className="gr-modal-acoes">
                      <button
                        type="button"
                        className="cv-botao gr-botao-perigo"
                        disabled={cancelando}
                        onClick={abrirCancelamento}
                      >
                        Cancelar operação…
                      </button>
                    </div>
                    <p className="cv-cinza cv-peq">
                      Registrado com autor, horário e motivo. Regerar é ação avançada — não é botão
                      de tela de operação.
                    </p>
                  </div>
                )}
                {previa && (
                  <div className="cv-cartao gr-vazio-grande">
                    <h2>Prévia do Giro de {diaCurto(dia)}</h2>
                    <p>
                      Clique em “Ver prévia” para ver produtos, blocos e responsáveis de amanhã com
                      as vendas lidas até agora. Nada é gravado — o Giro de verdade o automático
                      gera amanhã.
                    </p>
                  </div>
                )}
                {!previa && !rascunhos.length && !publicadas.length && (
                  <div className="cv-cartao gr-vazio-grande">
                    <h2>Nenhuma operação gerada para {diaCurto(dia)}</h2>
                    <p>
                      Escolha os setores à esquerda e simule. A simulação mostra faixas, cargas,
                      responsáveis sugeridos e exclusões — e não grava nada.
                    </p>
                  </div>
                )}
                {falha && (
                  <p className="cv-falha" role="alert">
                    {falha}
                  </p>
                )}
                {!previa && <QuemFezOQue op={op} />}
              </>
            )}
          </div>
        </div>
      )}
      {aCancelar && op && (
        <div className="cv-veu" onClick={fecharCancelamento}>
          <div
            className="gr-modal"
            role="dialog"
            aria-modal="true"
            aria-label="Cancelar a operação"
            onClick={(e) => e.stopPropagation()}
          >
            <h2>
              Cancelar a operação de {diaCurto(dia)} em {aCancelar.length}{' '}
              {aCancelar.length === 1 ? 'setor' : 'setores'}?
            </h2>
            <p className="cv-cinza cv-peq">{aCancelar.map((a) => a.nome).join(' · ')}</p>
            <p>
              O que já foi contado continua no histórico. A equipe perde o acesso à missão
              imediatamente.
            </p>
            <label className="gr-rotulo" htmlFor="gr-mot-canc">
              MOTIVO <b className="gr-vermelho">obrigatório</b>
            </label>
            <textarea
              id="gr-mot-canc"
              rows={3}
              value={motivo}
              maxLength={500}
              disabled={cancelando}
              onChange={(e) => setMotivo(e.target.value)}
              placeholder={`Ex.: base de vendas de ${diaCurto(op.vendasDe)} veio incompleta do HOS…`}
            />
            {falhaCancelar && (
              <p className="cv-falha" role="alert">
                {falhaCancelar}
              </p>
            )}
            <div className="gr-modal-acoes">
              <button
                type="button"
                className="cv-botao gr-botao-perigo"
                disabled={cancelando}
                onClick={cancelarTodas}
              >
                {cancelando ? 'Cancelando…' : 'Confirmar cancelamento'}
              </button>
              <button
                type="button"
                className="cv-botao"
                disabled={cancelando}
                onClick={fecharCancelamento}
              >
                Voltar
              </button>
            </div>
            <p className="cv-cinza cv-peq">Registrado com autor, horário e motivo.</p>
          </div>
        </div>
      )}
    </section>
  );
}

const diaSeguinte = (d: string) =>
  new Date(Date.parse(`${d}T12:00:00Z`) + 86_400_000).toISOString().slice(0, 10);

/** 'AAAA-MM-DD' de Brasília de um instante ISO (o `slice` do ISO daria o dia em UTC). */
const dataBr = (iso: string) =>
  new Date(iso).toLocaleDateString('sv-SE', { timeZone: 'America/Sao_Paulo' });

function Mini({
  rotulo,
  valor,
  verde,
  ambar,
}: {
  rotulo: string;
  valor: number | string;
  verde?: boolean;
  ambar?: boolean;
}) {
  return (
    <div className="cv-kpi">
      <div className="cv-kpi-rotulo">{rotulo}</div>
      <div className={`cv-kpi-valor ${verde ? 'gr-verde' : ''} ${ambar ? 'gr-ambar' : ''}`}>
        {valor}
      </div>
    </div>
  );
}

function QuemFezOQue({ op }: { op: OperacaoDoDia }) {
  const linhas = op.setores
    .flatMap((s) =>
      s.geracao
        ? [
            { em: s.geracao.criadoEm, texto: `Geração criada em rascunho · ${s.nome}` },
            ...(s.geracao.publicadoEm
              ? [
                  {
                    em: s.geracao.publicadoEm,
                    texto: `Publicação${s.geracao.publicadoPor ? ` por ${s.geracao.publicadoPor}` : ''} · ${s.nome}`,
                  },
                ]
              : []),
            ...s.blocos
              .filter((b) => b.transferencia)
              .map((b) => ({
                em: b.transferencia?.em ?? '',
                texto: `Transferência do ${bN(b.numero)} · ${b.transferencia?.deNome ?? ''} → ${b.responsavel?.nome ?? ''}`,
              })),
          ]
        : [],
    )
    .sort((a, b) => a.em.localeCompare(b.em));
  if (!linhas.length) return null;
  return (
    <div className="cv-cartao">
      <h2 className="cv-h2 cv-h2-topo">
        <Icone nome="shield" tamanho={15} traco={1.7} /> Quem fez o quê nesta operação
      </h2>
      <ul className="gr-auditoria">
        {linhas.map((l, k) => (
          <li key={k}>
            <span className="cv-mono cv-cinza">{hora(l.em)}</span>
            {l.texto}
          </li>
        ))}
      </ul>
    </div>
  );
}

/* ================================================================== *
 * 17C — Setores & blocos
 * ================================================================== */

export function SetoresEBlocos({ sessao }: { sessao: Sessao }) {
  const [d, setD] = useState<{
    setores: SetorDoGiro[];
    alvoGlobal: number;
    gruposSemSetor: string[];
  } | null>(null);
  const [erro, setErro] = useState(false);
  const [sel, setSel] = useState<string | null>(null);
  const [novo, setNovo] = useState(false);
  const [criandoPadrao, setCriandoPadrao] = useState(false);
  const [falhaPadrao, setFalhaPadrao] = useState<string | null>(null);
  const carregar = useCallback(() => {
    setErro(false);
    void lerSetores().then((r) => {
      if (r.ok) setD(r.dados);
      else setErro(true);
    });
  }, []);
  useEffect(carregar, [carregar]);
  const [salvoAgora, setSalvoAgora] = useState<string | null>(null);
  // sel ainda fora da lista (acabou de ser criado, recarregando): segue no editor atual, sem pular
  // para o primeiro setor
  const setor =
    d?.setores.find((s) => s.id === sel) ?? (novo || sel ? null : d?.setores[0]) ?? null;
  return (
    <section className="cv gr">
      <SubnavDoGiro atual="giro-setores" />
      <div className="cv-corpo">
        <div className="cv-topo">
          <div className="cv-titulo cv-titulo-2l">
            <h1>
              Setores &amp; blocos
              <Ajuda rotulo="setores e blocos">
                Toque num setor da lista para editar ao lado. Toda mudança aqui vale só para as
                próximas gerações — o Giro de hoje não muda.
              </Ajuda>
            </h1>
            <span>A quantidade de blocos é configurável por setor · alvo de exposição também</span>
          </div>
          <button
            type="button"
            className="cv-botao"
            onClick={() => {
              setNovo(true);
              setSel(null);
              setSalvoAgora(null);
            }}
          >
            <Icone nome="plus" tamanho={14} traco={1.8} /> Novo setor
          </button>
        </div>
        {erro ? (
          <ErroDeArea oQue="os setores" aoTentar={carregar} />
        ) : !d ? (
          <div className="cv-carregando" aria-busy="true" />
        ) : (
          <div className="gr-setores-grade">
            <div className="cv-cartao cv-sem-pad">
              <table className="cv-tabela">
                <thead>
                  <tr>
                    <th>Setor</th>
                    <th className="cv-dir">Blocos</th>
                    <th className="cv-dir">Alvo padrão</th>
                    <th className="cv-dir">Pessoas</th>
                    <th className="cv-dir">Última operação</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {d.setores.map((s) => (
                    <tr
                      key={s.id}
                      className={`cv-clicavel ${setor?.id === s.id ? 'cv-linha-sel' : ''} ${s.ativo ? '' : 'gr-linha-off'}`}
                      onClick={() => {
                        setSel(s.id);
                        setNovo(false);
                        setSalvoAgora(null);
                      }}
                    >
                      <td className="cv-forte">{s.nome}</td>
                      <td className="cv-dir cv-forte">
                        {s.blocosPorPessoas
                          ? `${String(Math.max(1, s.vinculados))} (1/pessoa)`
                          : s.qtdBlocos}
                      </td>
                      <td className="cv-dir">{s.alvo ?? d.alvoGlobal}</td>
                      <td className="cv-dir">{s.vinculados}</td>
                      <td className="cv-dir">
                        {s.ultimaOperacao ? diaCurto(s.ultimaOperacao) : '—'}
                      </td>
                      <td>
                        <span className={`gr-st ${s.ativo ? 'gr-st-ok' : ''}`}>
                          {s.ativo ? 'Ativo' : 'Desativado'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {d.setores.length === 0 && (
                <div className="cv-vazio">
                  <p>Nenhum setor ainda.</p>
                  <button
                    type="button"
                    className="cv-botao-primario"
                    disabled={criandoPadrao}
                    onClick={() => {
                      setCriandoPadrao(true);
                      setFalhaPadrao(null);
                      void criarSetoresPadrao(sessao).then((r) => {
                        setCriandoPadrao(false);
                        if (r.tipo === 'confirmada') carregar();
                        else
                          setFalhaPadrao(
                            r.tipo === 'recusada'
                              ? r.mensagem
                              : 'Sem resposta do servidor: os setores podem ter sido criados. Recarregue antes de repetir.',
                          );
                      });
                    }}
                  >
                    {criandoPadrao ? 'Criando…' : 'Criar os setores padrão'}
                  </button>
                  {falhaPadrao && (
                    <p className="cv-falha" role="alert">
                      {falhaPadrao}
                    </p>
                  )}
                  <p className="cv-cinza cv-peq">
                    Ético, genérico, similar, perfumaria, suplemento e alimentos — ligados aos
                    grupos do catálogo, com um bloco por pessoa. Depois é só vincular a equipe.
                  </p>
                </div>
              )}
              <div className="cv-rodape">
                <span>
                  Setor com histórico é desativado, não excluído — o mesmo vale para blocos.
                </span>
              </div>
              {d.gruposSemSetor.length > 0 && (
                <div className="cv-pad">
                  <span className="gr-rotulo">
                    GRUPOS DO CATÁLOGO SEM SETOR · {d.gruposSemSetor.length}
                  </span>
                  <p className="cv-cinza cv-peq">{d.gruposSemSetor.slice(0, 30).join(' · ')}</p>
                </div>
              )}
            </div>
            <EditorDeSetor
              key={setor?.id ?? 'novo'}
              sessao={sessao}
              setor={setor}
              salvoAgora={!!setor && setor.id === salvoAgora}
              alvoGlobal={d.alvoGlobal}
              grupos={d.gruposSemSetor}
              aoSalvar={(id) => {
                // o editor passa a mostrar o setor salvo — em "novo", salvar de novo duplicava
                const x = selecaoAposSalvar(id);
                setSel(x.sel);
                setNovo(x.novo);
                setSalvoAgora(id);
                carregar();
              }}
            />
          </div>
        )}
        {d && (
          <QuadroDeBlocos
            sessao={sessao}
            setores={d.setores}
            setorId={setor?.id ?? null}
            aoEscolherSetor={(id) => {
              setSel(id);
              setNovo(false);
              setSalvoAgora(null);
            }}
          />
        )}
        <ConfiguracoesDoGiro />
      </div>
    </section>
  );
}

/**
 * A captura em português, do jeito que o gestor fala — REGRA FIXA (dono, 01/10): só vendidos
 * ontem; segunda pega sábado e domingo; nunca controlado, Ozempic, Mounjaro ou geladeira.
 */
function textoDaCaptura(total: string): string {
  const teto = total
    ? `no máximo ${total} produtos por dia (os que mais venderam primeiro)`
    : 'todos os que venderam';
  return `Entram só os produtos VENDIDOS ONTEM (segunda-feira: sábado e domingo) · ${teto}. Nenhum produto sem venda entra. Controlados, antibióticos, Ozempic, Mounjaro e geladeira ficam sempre fora.`;
}

function EditorDeSetor({
  sessao,
  setor,
  salvoAgora = false,
  alvoGlobal,
  grupos,
  aoSalvar,
}: {
  sessao: Sessao;
  setor: SetorDoGiro | null;
  salvoAgora?: boolean;
  alvoGlobal: number;
  grupos: readonly string[];
  aoSalvar: (id: string) => void;
}) {
  const [nome, setNome] = useState(setor?.nome ?? '');
  const [lista, setLista] = useState((setor?.grupos ?? []).join(', '));
  const [classif, setClassif] = useState((setor?.classificacoes ?? []).join(', '));
  const [qtd, setQtd] = useState(setor?.qtdBlocos ?? 1);
  // Setor novo nasce com um bloco por pessoa (dono, 26/09).
  const [porPessoas, setPorPessoas] = useState(setor?.blocosPorPessoas ?? true);
  const [divisao, setDivisao] = useState<'LETRA' | 'LABORATORIO'>(setor?.divisao ?? 'LETRA');
  // Regra fixa (dono, 01/10): 100% vendidos, janela = ontem. Só o teto do dia é do setor.
  const cap0 = setor?.captura ?? { pctVendidos: 100, diasInicio: 1, diasFim: 1, total: null };
  const [total, setTotal] = useState(cap0.total === null ? '' : String(cap0.total));
  const [alvo, setAlvo] = useState<string>(
    setor?.alvo === null || setor?.alvo === undefined ? '' : String(setor.alvo),
  );
  const [quebra, setQuebra] = useState(setor?.permitirQuebraLetra ?? false);
  const [ean, setEan] = useState(setor?.exigeEan ?? true);
  const [foto, setFoto] = useState(setor?.fotoTaxa ?? 0);
  const [ativo, setAtivo] = useState(setor?.ativo ?? true);
  const [falha, setFalha] = useState<string | null>(null);
  const [salvo, setSalvo] = useState(salvoAgora);
  const [salvando, setSalvando] = useState(false);
  // Setor recém-criado, antes de a lista recarregar: salvar de novo ATUALIZA este, não cria outro.
  const [criado, setCriado] = useState<{ id: string; versao: number } | null>(null);
  const salvar = () => {
    if (salvando) return;
    setFalha(null);
    setSalvo(false);
    setSalvando(true);
    void salvarSetor(sessao, {
      id: setor?.id ?? criado?.id ?? null,
      versaoEsperada: setor?.versao ?? criado?.versao ?? null,
      nome: nome.trim(),
      descricao: setor?.descricao ?? null,
      grupos: lista
        .split(',')
        .map((g) => g.trim())
        .filter(Boolean),
      classificacoes: classif
        .split(',')
        .map((g) => g.trim())
        .filter(Boolean),
      ordem: setor?.ordem ?? 0,
      qtdBlocos: qtd,
      blocosPorPessoas: porPessoas,
      divisao,
      captura: {
        pctVendidos: 100,
        diasInicio: 1,
        diasFim: 1,
        total: total === '' ? null : Number(total),
      },
      permitirQuebraLetra: quebra,
      alvo: alvo === '' ? null : Number(alvo),
      exigeEan: ean,
      fotoTaxa: foto,
      gera: setor?.gera ?? true,
      ativo,
    }).then((r) => {
      setSalvando(false);
      if (r.tipo === 'confirmada') {
        setSalvo(true);
        if (!setor) setCriado(r.dados);
        aoSalvar(r.dados.id);
      } else if (r.tipo === 'recusada') setFalha(r.mensagem);
      else setFalha('Sem resposta do servidor: pode ter sido salvo. Recarregue antes de repetir.');
    });
  };
  const desativa = setor ? setor.blocos.filter((b) => b.ativo && b.numero > qtd) : [];
  return (
    <div className="cv-cartao gr-editor">
      <div className="cv-cartao-topo">
        <h2>{setor ? `Editar ${setor.nome}` : 'Novo setor'}</h2>
        {setor && <span className="cv-mono cv-cinza cv-peq">versão {setor.versao}</span>}
      </div>
      <label className="gr-rotulo" htmlFor="gr-nome">
        NOME
      </label>
      <input
        id="gr-nome"
        className="gr-input"
        value={nome}
        onChange={(e) => setNome(e.target.value)}
        maxLength={60}
      />
      <label className="gr-rotulo" htmlFor="gr-grupos">
        GRUPOS DO CATÁLOGO (separados por vírgula)
        <Ajuda rotulo="grupos do catálogo">
          Os grupos de produto do HOS que pertencem a este setor (ex.: Eticos, Genericos). Um
          produto só entra no Giro se o grupo dele estiver em algum setor.
        </Ajuda>
      </label>
      <input
        id="gr-grupos"
        className="gr-input"
        value={lista}
        onChange={(e) => setLista(e.target.value)}
        list="gr-grupos-sem"
      />
      <datalist id="gr-grupos-sem">
        {grupos.map((g) => (
          <option key={g} value={g} />
        ))}
      </datalist>
      <label className="gr-rotulo" htmlFor="gr-classif">
        SÓ ESTAS CLASSIFICAÇÕES (opcional, separadas por vírgula)
        <Ajuda rotulo="classificações">
          Para dividir um grupo grande em mais de um setor: só entram as classificações do HOS
          escritas aqui. Vazio = o grupo inteiro.
        </Ajuda>
      </label>
      <input
        id="gr-classif"
        className="gr-input"
        value={classif}
        placeholder="vazio = o grupo inteiro"
        onChange={(e) => setClassif(e.target.value)}
      />

      <span className="gr-rotulo">
        QUAIS PRODUTOS ENTRAM NO GIRO
        <Ajuda rotulo="quais produtos entram">
          Entram os produtos do setor que venderam ontem. Com teto, entram só os que mais venderam
          até esse número; sem teto, todos.
        </Ajuda>
      </span>
      <div className="gr-captura">
        <div className="gr-duas">
          <label>
            <span className="gr-rotulo">TETO DE PRODUTOS POR DIA NO SETOR</span>
            <input
              className="gr-input"
              inputMode="numeric"
              value={total}
              placeholder="sem teto = todos que venderam"
              onChange={(e) => setTotal(e.target.value.replace(/\D/g, ''))}
            />
          </label>
        </div>
        <p className="cv-cinza cv-peq">{textoDaCaptura(total)}</p>
      </div>

      <span className="gr-rotulo">
        DIVISÃO DOS BLOCOS
        <Ajuda rotulo="divisão dos blocos">
          Como o setor é repartido entre as pessoas. Por letra: cada bloco pega uma faixa do
          alfabeto (A→G, H→P…) com quantidade parecida. Por laboratório: cada bloco pega
          laboratórios inteiros.
        </Ajuda>
      </span>
      <div className="gr-chips" role="radiogroup" aria-label="Divisão dos blocos">
        {(
          [
            ['LETRA', 'Por letra (A–Z equilibrado)'],
            ['LABORATORIO', 'Por laboratório'],
          ] as const
        ).map(([v, r]) => (
          <button
            key={v}
            type="button"
            role="radio"
            aria-checked={divisao === v}
            className={divisao === v ? 'gr-chip gr-chip-sel' : 'gr-chip'}
            onClick={() => setDivisao(v)}
          >
            {r}
          </button>
        ))}
      </div>
      {divisao === 'LABORATORIO' && (
        <p className="cv-cinza cv-peq">
          Os laboratórios são repartidos iguais entre os blocos (ex.: 23 labs em 3 blocos = 8, 8 e
          7), e todos os produtos de um laboratório ficam no mesmo bloco.
        </p>
      )}

      <div className="gr-toggles">
        <label>
          <input
            type="checkbox"
            checked={porPessoas}
            onChange={(e) => setPorPessoas(e.target.checked)}
          />{' '}
          Um bloco por pessoa do setor
          <em>
            {setor
              ? `Hoje: ${String(setor.vinculados)} pessoa(s) vinculada(s) = ${String(Math.max(1, setor.vinculados))} bloco(s).`
              : 'Cada pessoa vinculada ao setor é um bloco.'}
          </em>
        </label>
      </div>
      {!porPessoas && (
        <span className="gr-rotulo">
          NÚMERO DE BLOCOS
          <Ajuda rotulo="número de blocos">
            Em quantas partes o setor é dividido por dia. Cada bloco vai para uma pessoa presente.
          </Ajuda>
        </span>
      )}
      {!porPessoas && (
        <div className="gr-stepper-linha">
          <div className="gr-stepper">
            <button
              type="button"
              aria-label="Menos um bloco"
              onClick={() => setQtd((q) => Math.max(1, q - 1))}
            >
              <Icone nome="minus" tamanho={14} traco={1.8} />
            </button>
            <b>{qtd}</b>
            <button
              type="button"
              aria-label="Mais um bloco"
              onClick={() => setQtd((q) => Math.min(50, q + 1))}
            >
              <Icone nome="plus" tamanho={14} traco={1.8} />
            </button>
          </div>
          {setor && (
            <span className="cv-cinza cv-peq">
              Atual em produção: <b>{setor.qtdBlocos}</b>
              <br />
              inteiro ≥ 1
            </span>
          )}
        </div>
      )}
      {setor && !porPessoas && qtd !== setor.qtdBlocos && (
        <div className="gr-aviso-ambar">
          <Icone nome="warn" tamanho={14} traco={1.7} />
          <span>
            A alteração será aplicada <b>somente às próximas gerações</b>. A operação de hoje
            continua com {setor.qtdBlocos}.
          </span>
        </div>
      )}
      <div className="gr-toggles">
        <label>
          <input type="checkbox" checked={quebra} onChange={(e) => setQuebra(e.target.checked)} />{' '}
          Quebrar a mesma letra entre blocos
          <Ajuda rotulo="quebrar a letra">
            Ligado, uma letra com muitos produtos pode ser dividida entre dois blocos para
            equilibrar. Desligado, a letra fica inteira num bloco só.
          </Ajuda>
          <em>Desligado, a fronteira respeita sempre a letra inteira.</em>
        </label>
        <label>
          <input type="checkbox" checked={ean} onChange={(e) => setEan(e.target.checked)} /> Exigir
          leitura do código de barras
          <Ajuda rotulo="exigir código de barras">
            Ligado, o balconista precisa ler (ou digitar) o código de barras do produto antes de
            contar — garante que está contando o produto certo.
          </Ajuda>
        </label>
        <label>
          <input type="checkbox" checked={ativo} onChange={(e) => setAtivo(e.target.checked)} />{' '}
          Setor ativo
          <Ajuda rotulo="setor ativo">
            Desligado, o setor para de gerar Giro. Nada é apagado: o histórico continua.
          </Ajuda>
        </label>
      </div>
      <div className="gr-duas">
        <label>
          <span className="gr-rotulo">
            ALVO DE EXPOSIÇÃO
            <Ajuda rotulo="alvo de exposição">
              Quantas unidades de cada produto o setor quer na prateleira. Em branco, usa o alvo
              geral das Configurações.
            </Ajuda>
          </span>
          <input
            className="gr-input"
            inputMode="numeric"
            value={alvo}
            placeholder={`global: ${String(alvoGlobal)}`}
            onChange={(e) => setAlvo(e.target.value.replace(/\D/g, ''))}
          />
        </label>
        <label>
          <span className="gr-rotulo">
            FOTO DE AUDITORIA (%)
            <Ajuda rotulo="foto de auditoria">
              Em quantos por cento dos produtos o sistema sorteia um pedido de foto da prateleira. 0
              = nunca pede. Só vale com &quot;Pedir foto&quot; ligado nas Configurações.
            </Ajuda>
          </span>
          <input
            className="gr-input"
            inputMode="numeric"
            value={String(foto)}
            onChange={(e) => setFoto(Math.min(100, Number(e.target.value.replace(/\D/g, '') || 0)))}
          />
        </label>
      </div>
      {setor && (
        <>
          <span className="gr-rotulo">
            BLOCOS DESTE SETOR
            <Ajuda rotulo="blocos deste setor">
              Os blocos que já existiram neste setor e o último dia em que foram usados.
            </Ajuda>
          </span>
          <ul className="gr-lista-blocos">
            {setor.blocos.map((b) => (
              <li key={b.numero}>
                <b>{bN(b.numero)}</b>
                <span>{b.ultimoUso ? `usado ${diaCurto(b.ultimoUso)}` : 'ainda não usado'}</span>
                <span style={{ flex: 1 }} />
                <span
                  className={`gr-st ${desativa.includes(b) ? 'gr-st-rep' : b.ativo ? 'gr-st-ok' : ''}`}
                >
                  {desativa.includes(b) ? 'Aviso' : b.ativo ? 'Ativo' : 'Desativado'}
                </span>
              </li>
            ))}
          </ul>
          <Vinculos sessao={sessao} setorId={setor.id} />
          {desativa.length > 0 && (
            <p className="cv-cinza cv-peq">
              {desativa.map((b) => bN(b.numero)).join(', ')} seria(m) desativado(s) ao salvar —
              nunca apagados.
            </p>
          )}
          <p className="cv-cinza cv-peq">
            A faixa (A→D, E→G) não é configurável aqui: ela é consequência de cada geração.
          </p>
        </>
      )}
      {falha && (
        <p className="cv-falha" role="alert">
          {falha}
        </p>
      )}
      {salvo && <p className="gr-ok-txt">Salvo. Vale para as próximas gerações.</p>}
      <div className="gr-modal-acoes">
        <button
          type="button"
          className="cv-botao-primario gr-botao-grande"
          onClick={salvar}
          disabled={!nome.trim() || salvando}
        >
          {salvando ? 'Salvando…' : 'Salvar para próximas'}
        </button>
      </div>
    </div>
  );
}

export function ConfiguracoesDoGiro() {
  const [c, setC] = useState<ConfigDoGiro | null>(null);
  useEffect(() => {
    void lerConfigDoGiro().then((r) => r.ok && setC(r.dados));
  }, []);
  if (!c) return null;
  return (
    <div className="cv-cartao gr-config" id="config">
      <div className="cv-cartao-topo">
        <h2>Configurações do Giro</h2>
        <span className="gr-restrito">
          <Icone nome="lock" tamanho={12} traco={1.7} /> Área restrita · administrador
        </span>
      </div>
      <ul className="gr-config-lista">
        <li>
          <div>
            <b>Base de vendas</b>
            <em>
              Só produtos vendidos no dia anterior · segunda-feira pega sábado e domingo (domingo
              não tem Giro) · regra fixa
            </em>
          </div>
          <span className="gr-valor">ontem</span>
        </li>
        <li>
          <div>
            <b>Nunca entram</b>
            <em>
              Controlados (ético ou genérico, também pela molécula quando o HOS classifica só
              algumas dosagens), antibióticos, déficit de atenção, Ozempic, Mounjaro e geladeira
              {c.fora_do_giro_nomes?.length
                ? ` · mais ${String(c.fora_do_giro_nomes.length)} nome(s) da Configuração`
                : ''}
            </em>
          </div>
          <span className="gr-valor">fixo</span>
        </li>
        <li>
          <div>
            <b>Estoque mínimo para entrar na missão</b>
            <em>Produto com estoque confiável abaixo disso fica fora</em>
          </div>
          <span className="gr-valor">{c.estoque_minimo}</span>
        </li>
        <li>
          <div>
            <b>Idade máxima do estoque</b>
            <em>Mais velho que isso é "desatualizado" — nunca zero</em>
          </div>
          <span className="gr-valor">{c.estoque_idade_max_min} min</span>
        </li>
        <li>
          <div>
            <b>Alvo global de exposição</b>
            <em>Vale quando o setor não tem alvo próprio</em>
          </div>
          <span className="gr-valor">{c.alvo_global}</span>
        </li>
        <li>
          <div>
            <b>Presença parcial</b>
            <em>Peso de quem está meio período</em>
          </div>
          <span className="gr-valor">{String(c.parcial_fator).replace('.', ',')}</span>
        </li>
        <li>
          <div>
            <b>Giro primeiro, InovaPay depois</b>
            <em>
              Com a regra ligada, o balconista com Giro pendente só registra venda depois de
              concluir
            </em>
          </div>
          <span className={`gr-valor ${c.bloqueia_inovapay ? 'gr-verde' : 'cv-cinza'}`}>
            {c.bloqueia_inovapay ? 'ligada' : 'desligada'}
          </span>
        </li>
        <li>
          <div>
            <b>Geração automática</b>
            <em>
              {c.geracao_automatica
                ? `Gera o rascunho a partir das ${c.geracao_horario} nos dias marcados · publicar continua sendo de uma pessoa`
                : 'Desligada · a operação é gerada à mão nesta tela'}
            </em>
          </div>
          <span className={`gr-valor ${c.geracao_automatica ? 'gr-verde' : 'cv-cinza'}`}>
            {c.geracao_automatica ? 'ligada' : 'desligada'}
          </span>
        </li>
        <li>
          <div>
            <b>Fonte de estoque</b>
            <em>Leitura do HOS · sem leitura confiável o item fica fora, nunca vira zero</em>
          </div>
          <span className="gr-valor">API do HOS</span>
        </li>
      </ul>
      <div className="gr-formula">
        <b>Fórmula de reposição vigente</b>
        <pre>
          {
            'falta_para_alvo = max(0, alvo − prateleira)\ndisponivel_fora = max(0, estoque_sistema − prateleira)\nrepor = max(0, min(falta_para_alvo, disponivel_fora))'
          }
        </pre>
        <em>Exibida aqui para conferência. A fórmula é do servidor — a tela não recalcula.</em>
      </div>
      <p className="cv-cinza cv-peq">
        <Icone nome="shield" tamanho={12} traco={1.6} /> Configuração v{c.versao} · histórico é
        somente leitura. Para mudar, use <a href="#/giro-config">Configurações do Giro</a> — cada
        mudança vira uma versão nova, com motivo.
      </p>
    </div>
  );
}

export function Vinculos({
  sessao,
  setorId,
  aoMudar,
}: {
  sessao: Sessao;
  setorId: string;
  aoMudar?: () => void;
}) {
  const [membros, setMembros] = useState<
    readonly { accountId: string; nome: string; ate: string | null }[] | null
  >(null);
  const [equipe, setEquipe] = useState<readonly MembroDaEquipe[]>([]);
  const [escolhido, setEscolhido] = useState('');
  const [falha, setFalha] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);
  const carregar = useCallback(() => {
    void lerEquipe(hojeBrasilia(), setorId).then(
      (r) =>
        r.ok &&
        setMembros(
          r.dados.pessoas.map((p) => ({ accountId: p.accountId, nome: p.nome, ate: p.vinculoAte })),
        ),
    );
  }, [setorId]);
  useEffect(carregar, [carregar]);
  useEffect(() => {
    void listarEquipe().then(
      (r) =>
        r.ok && setEquipe(r.dados.pessoas.filter((p) => p.accountId && p.situacao === 'ativo')),
    );
  }, []);
  const livres = equipe.filter((p) => !membros?.some((m) => m.accountId === p.accountId));
  const acao = (f: () => Promise<{ tipo: string; mensagem?: string }>) => {
    if (enviando) return;
    setFalha(null);
    setEnviando(true);
    void f().then((r) => {
      setEnviando(false);
      if (r.tipo === 'confirmada') {
        setEscolhido('');
        carregar();
        aoMudar?.();
      } else
        setFalha(
          r.tipo === 'recusada'
            ? (r.mensagem ?? 'Não foi aceito.')
            : 'Sem resposta do servidor. Recarregue antes de repetir.',
        );
    });
  };
  return (
    <>
      <span className="gr-rotulo">
        PESSOAS VINCULADAS · {membros ? membros.filter((m) => !m.ate).length : '—'}
        <Ajuda rotulo="pessoas vinculadas">
          Quem trabalha neste setor e pode receber bloco dele. Encerrar o vínculo vale a partir de
          amanhã; o histórico da pessoa fica.
        </Ajuda>
      </span>
      <ul className="gr-lista-blocos">
        {membros?.map((m) => (
          <li key={m.accountId}>
            <b>{m.nome}</b>
            {m.ate && <span className="cv-cinza cv-peq">sai do setor depois de hoje</span>}
            <span style={{ flex: 1 }} />
            {m.ate ? (
              // o vínculo encerrado hoje ainda vale hoje: religar reabre o mesmo (servidor)
              <button
                type="button"
                className="gr-link gr-link-botao"
                disabled={enviando}
                onClick={() => acao(() => vincular(sessao, setorId, m.accountId))}
              >
                Manter no setor
              </button>
            ) : (
              <button
                type="button"
                className="gr-link gr-link-botao"
                disabled={enviando}
                onClick={() => acao(() => encerrarVinculo(sessao, setorId, m.accountId))}
              >
                Encerrar vínculo
              </button>
            )}
          </li>
        ))}
        {membros && !membros.length && <li>Ninguém vinculado ainda.</li>}
      </ul>
      <div className="gr-vincular">
        <select
          value={escolhido}
          onChange={(e) => setEscolhido(e.target.value)}
          aria-label="Pessoa para vincular"
        >
          <option value="">Vincular pessoa…</option>
          {livres.map((p) => (
            <option key={p.accountId} value={p.accountId ?? ''}>
              {p.nome}
            </option>
          ))}
        </select>
        <button
          type="button"
          className="cv-botao"
          disabled={!escolhido || enviando}
          onClick={() => acao(() => vincular(sessao, setorId, escolhido))}
        >
          Vincular
        </button>
      </div>
      {falha && (
        <p className="cv-falha" role="alert">
          {falha}
        </p>
      )}
      <p className="cv-cinza cv-peq">
        Encerrar vale a partir de amanhã: a pessoa deixa de receber missão e de segurar ciclo. O
        histórico fica.
      </p>
    </>
  );
}
